import {
  generateProcessConfigToken,
  queryProcessConfigSummary,
  queryProcessById,
  queryProcessVersions,
  queryActionsDiagram,
} from './service';
import _ from 'lodash';
import { CoreEnums } from '@/utils/domain/constants';

const Model = {
  namespace: 'processConfig',

  state: {
    id: null,
    searchConfig: '',
    processSearchCriteria: '',
    selectedKeys: '',
    processId: null,
    processData: null, // Store process details including name
    processVersions: [], // Store available process versions
    generatedToken: null,
    versionId: null, // Added versionId to state
    accessMode: null,
    listConfig: [],
    newPermission: false,
    searchConfigLoading: false,
    blades: [],
    isSiderCollapsed: false,
    siderManuallyExpanded: false, // Track if user manually expanded when auto-collapsed
  },

  effects: {
    *fetchProcessConfig({ payload }, { call, put }) {
      try {
        // First, fetch process details to get the name
        const processData = yield call(queryProcessById, payload.processId);

        if (processData) {
          yield put({
            type: 'storeProcessData',
            payload: processData,
          });
        }

        // Fetch available versions
        const versionsResponse = yield call(queryProcessVersions, payload.processId);

        if (versionsResponse && versionsResponse.items) {
          yield put({
            type: 'storeProcessVersions',
            payload: versionsResponse.items,
          });
        }

        // Then, generate token for the process
        const tokenResponse = yield call(
          generateProcessConfigToken,
          payload.processId,
          CoreEnums.AccessMode.ReadOnly,
        );

        if (tokenResponse && tokenResponse.token) {
          // Store the token and versionId
          yield put({
            type: 'queryGeneratedToken',
            payload: {
              token: tokenResponse.token,
              versionId: tokenResponse.versionId,
              versionNumber: tokenResponse.versionNumber,
              accessMode: CoreEnums.AccessMode.ReadOnly,
              processId: payload.processId, // Add processId to the payload
            },
          });

          // Now fetch the summary using the token and versionId
          const summaryResponse = yield call(
            queryProcessConfigSummary,
            payload.processId,
            tokenResponse.versionId,
            tokenResponse.token,
          );

          if (summaryResponse) {
            yield put({
              type: 'queryProcessConfig',
              payload: summaryResponse,
            });
          }
        }
      } catch (error) {
        // Silent fail to avoid leaking sensitive data
      }
    },

    *changeProcessVersion({ payload }, { call, put, select }) {
      try {
        const { versionNumber, processId } = payload;
        const currentState = yield select((state) => state.processConfig);

        // Close all action-related blades when changing version
        const blades = currentState.blades || [];
        const filteredBlades = blades.filter(
          (blade) =>
            !blade.key.startsWith('action-') &&
            blade.key !== 'actions-root' &&
            blade.key !== 'actions-menu',
        );

        yield put({
          type: 'updateBlades',
          payload: filteredBlades,
        });

        // Generate token for the selected version
        const tokenResponse = yield call(
          generateProcessConfigToken,
          processId,
          CoreEnums.AccessMode.ReadOnly,
        );

        if (tokenResponse && tokenResponse.token) {
          yield put({
            type: 'queryGeneratedToken',
            payload: {
              token: tokenResponse.token,
              versionId: tokenResponse.versionId,
              versionNumber: tokenResponse.versionNumber,
              accessMode: CoreEnums.AccessMode.ReadOnly,
              processId: processId,
              // No reopenActionId - we always close blades when changing version
            },
          });

          // Clear the active action when changing version
          yield put({
            type: 'processActionConfig/clearActiveAction',
          });

          // Fetch the summary for the new version
          const summaryResponse = yield call(
            queryProcessConfigSummary,
            processId,
            tokenResponse.versionId,
            tokenResponse.token,
          );

          if (summaryResponse) {
            yield put({
              type: 'queryProcessConfig',
              payload: summaryResponse,
            });
          }
        }
      } catch (error) {
        // Silent fail to avoid leaking sensitive data
      }
    },

    *closeNewProcess({ payload }, { put }) {
      yield put({
        type: 'closeProcess',
        payload,
      });
    },

    *showItemProcesss({ view }, { put }) {
      yield put({
        type: 'showEditItemProcess',
        view,
      });
    },

    *processConfigFilter({ payload }, { put }) {
      yield put({
        type: 'searchConfig',
        payload,
      });
    },
    *processConfigFilterLoading({ payload = 'searchConfigLoading' }, { put }) {
      yield put({
        type: payload,
      });
    },
    *processSearch({ payload }, { put }) {
      yield put({
        type: 'processSearchCriteria',
        payload,
      });
    },

    *hideAllForms({ payload = 'hideForms' }, { put }) {
      yield put({ type: payload });
    },
    *resetConfig({ payload = 'resetConfigs' }, { put }) {
      yield put({ type: payload });
    },

    *selectedKey({ payload }, { put }) {
      yield put({
        type: 'selectedKeys',
        payload,
      });
    },

    *toggleAccessMode({ payload }, { call, put, select }) {
      try {
        const { processId } = payload;
        const currentState = yield select((state) => state.processConfig);
        const actionConfigState = yield select((state) => state.processActionConfig);
        const currentAccessMode = currentState.accessMode || CoreEnums.AccessMode.ReadOnly;

        // Toggle between ReadOnly and Write
        const newAccessMode =
          currentAccessMode === CoreEnums.AccessMode.ReadOnly
            ? CoreEnums.AccessMode.Write
            : CoreEnums.AccessMode.ReadOnly;

        // Generate new token with the new access mode
        const tokenResponse = yield call(generateProcessConfigToken, processId, newAccessMode);

        if (tokenResponse && tokenResponse.token) {
          // Get currently active action ID from processActionConfig
          const openActionId = actionConfigState.activeActionId;

          // Close all action-related blades to force refresh with new token
          const blades = currentState.blades || [];
          const filteredBlades = blades.filter(
            (blade) =>
              !blade.key.startsWith('action-') &&
              blade.key !== 'actions-root' &&
              blade.key !== 'actions-menu',
          );

          yield put({
            type: 'updateBlades',
            payload: filteredBlades,
          });

          // Update the token and access mode
          const payload = {
            token: tokenResponse.token,
            versionId: tokenResponse.versionId,
            versionNumber: tokenResponse.versionNumber,
            accessMode: newAccessMode,
            processId: processId,
          };

          // Only reopen action if:
          // 1. There was an action open (openActionId exists)
          // 2. We're switching FROM ReadOnly TO Write (not the other way around)
          if (openActionId && newAccessMode === CoreEnums.AccessMode.Write) {
            payload.reopenActionId = openActionId;
          }

          yield put({
            type: 'queryGeneratedToken',
            payload,
          });

          // Refresh diagram with new token to ensure all components use the updated token
          const diagramResponse = yield call(
            queryActionsDiagram,
            processId,
            tokenResponse.versionId,
            tokenResponse.token,
          );

          if (diagramResponse) {
            yield put({
              type: 'processActionConfig/queryActionsFromDiagram',
              payload: diagramResponse,
            });
          }

          // If switching to ReadOnly, clear the active action
          if (newAccessMode === CoreEnums.AccessMode.ReadOnly) {
            yield put({
              type: 'processActionConfig/clearActiveAction',
            });
          }
        }
      } catch (error) {
        // Silent fail to avoid leaking sensitive data
      }
    },
  },

  reducers: {
    pushBlade(state, { payload }) {
      const blades = state.blades || [];
      // Check if blade with same key already exists
      const existingIndex = blades.findIndex((blade) => blade.key === payload.key);

      if (existingIndex !== -1) {
        // Replace existing blade
        const newBlades = [...blades];
        newBlades[existingIndex] = payload;
        return { ...state, blades: newBlades };
      }

      // Add new blade
      return { ...state, blades: [...blades, payload] };
    },

    // Navigate to a blade - clears all except sider and pushes the new blade
    navigateToBlade(state, { payload }) {
      const blades = state.blades || [];

      // Check if we're trying to navigate to the same blade that's already the only non-sider blade
      const nonSiderBlades = blades.filter((blade) => blade.key !== 'sider');
      if (nonSiderBlades.length === 1 && nonSiderBlades[0].key === payload.key) {
        // Same blade is already open as the only navigation blade, no need to change anything
        return state;
      }

      // Keep only the sider blade (first blade)
      const siderBlade = blades.find((blade) => blade.key === 'sider');
      const newBlades = siderBlade ? [siderBlade] : [];

      // Add the new blade
      newBlades.push(payload);
      return { ...state, blades: newBlades };
    },
    replaceTopBlade(state, { payload }) {
      const blades = [...(state.blades || [])];
      if (blades.length) blades[blades.length - 1] = payload;
      return { ...state, blades };
    },
    updateBladesCollapse(state, { payload }) {
      const blades = (state.blades || []).map((b) =>
        b && b.collapsedWidth != null ? { ...b, collapsed: payload } : b,
      );
      return { ...state, blades };
    },
    popBlade(state) {
      const blades = [...(state.blades || [])];
      blades.pop();
      return { ...state, blades };
    },
    removeBlade(state, { payload }) {
      const blades = (state.blades || []).filter((blade) => blade.key !== payload.key);
      return { ...state, blades };
    },
    closeBladesFromIndex(state, { payload }) {
      const { fromIndex } = payload;
      const blades = state.blades || [];

      // Keep only blades up to (but not including) the fromIndex
      // This closes the blade at fromIndex and all blades after it (children)
      const newBlades = blades.slice(0, fromIndex);

      return { ...state, blades: newBlades };
    },
    replaceActionBlade(state, { payload }) {
      const blades = state.blades || [];
      // Remove all existing action form blades (keys starting with 'action-')
      const filteredBlades = blades.filter((blade) => !blade.key.startsWith('action-'));
      // Add the new action blade
      return { ...state, blades: [...filteredBlades, payload] };
    },
    replaceActionFormBlade(state, { payload }) {
      const blades = state.blades || [];
      const { actionId, key } = payload;

      // Remove only the form blade for this specific action
      const filteredBlades = blades.filter(
        (blade) => blade.key !== key && !blade.key.startsWith(`action-${actionId}-form`),
      );

      // Add the new blade
      return { ...state, blades: [...filteredBlades, payload] };
    },
    addActionBlade(state, { payload }) {
      const blades = state.blades || [];
      const { key } = payload;

      // Check if blade already exists
      const existingIndex = blades.findIndex((blade) => blade.key === key);

      if (existingIndex >= 0) {
        // Blade exists, move it to the end (make it visible)
        const newBlades = [...blades];
        const [existingBlade] = newBlades.splice(existingIndex, 1);
        newBlades.push(existingBlade);
        return { ...state, blades: newBlades };
      } else {
        // Blade doesn't exist, add it
        return { ...state, blades: [...blades, payload] };
      }
    },
    showActionBlade(state, { payload }) {
      const blades = state.blades || [];
      const key = payload;

      // Find the blade and move it to the end (make it visible)
      const existingIndex = blades.findIndex((blade) => blade.key === key);

      if (existingIndex >= 0) {
        const newBlades = [...blades];
        const [existingBlade] = newBlades.splice(existingIndex, 1);
        newBlades.push(existingBlade);
        return { ...state, blades: newBlades };
      }

      return state;
    },
    updateBladeTitle(state, { payload }) {
      const { key, title } = payload;
      const blades = state.blades || [];

      const updatedBlades = blades.map((blade) => {
        if (blade.key === key) {
          return { ...blade, title };
        }
        return blade;
      });

      return { ...state, blades: updatedBlades };
    },
    updateBlades(state, { payload }) {
      return { ...state, blades: payload };
    },
    clearReopenActionId(state) {
      return {
        ...state,
        generatedToken: {
          ...state.generatedToken,
          reopenActionId: null,
        },
      };
    },
    handleActionClick(state, { payload }) {
      const { actionsRootBlade, actionFormBlade } = payload;
      const blades = state.blades || [];

      // Start with current blades
      let newBlades = [...blades];

      // Remove any existing actions-related blades (root or menu)
      newBlades = newBlades.filter(
        (blade) =>
          blade.key !== 'actions-root' &&
          blade.key !== 'actions-menu' &&
          !blade.key.startsWith('action-'),
      );

      // Add the actions root blade
      newBlades.push(actionsRootBlade);

      // Add the action form blade only if provided (optional for new pattern)
      if (actionFormBlade) {
        newBlades.push(actionFormBlade);
      }

      return { ...state, blades: newBlades };
    },
    clearBlades(state) {
      return { ...state, blades: [] };
    },
    setSiderCollapsed(state, { payload }) {
      // payload can be boolean or object { collapsed: boolean, manual: boolean }
      if (typeof payload === 'boolean') {
        return { ...state, isSiderCollapsed: payload };
      }

      const { collapsed, manual = false } = payload;
      return {
        ...state,
        isSiderCollapsed: collapsed,
        siderManuallyExpanded:
          manual && !collapsed ? true : manual ? false : state.siderManuallyExpanded,
      };
    },
    storeProcessData(state, { payload }) {
      return {
        ...state,
        processData: payload,
      };
    },
    storeProcessVersions(state, { payload }) {
      return {
        ...state,
        processVersions: payload,
      };
    },
    queryGeneratedToken(state, { payload }) {
      return {
        ...state,
        generatedToken: payload, // Store the entire payload object
        versionId: payload.versionId,
        accessMode: payload.accessMode,
        processId: payload.processId, // Add processId to state
      };
    },

    queryProcessConfig(state, { payload }) {
      return {
        ...state,
        listConfig: payload, // payload is now the summary response directly
        processId: state.processId,
      };
    },

    closeProcess(state, { payload }) {
      return {
        ...state,
        newProcess: payload.state,
        selectedItem: state.selectedItem,
        view: state.view,
        id: state.id,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    searchConfig(state, { payload }) {
      return {
        ...state,
        searchConfig: payload,
        searchConfigLoading: true,
      };
    },
    searchConfigLoading(state) {
      return {
        ...state,
        searchConfigLoading: false,
      };
    },
    processSearchCriteria(state, { payload }) {
      return {
        ...state,
        processSearchCriteria: payload,
      };
    },

    selectedKeys(state, { payload }) {
      return {
        ...state,
        selectedKeys: state.selectedKeys === payload ? '' : payload,
      };
    },

    resetConfigs(state) {
      return {
        ...state,
        searchConfig: '',
        searchConfigLoading: false,
        processSearchCriteria: '',
        selectedKeys: '',
        listConfig: [],
      };
    },

    resetConfig(state) {
      return {
        ...state,
        processId: null,
        processData: null,
        processVersions: [],
        generatedToken: null,
        versionId: null,
        accessMode: null,
        listConfig: [],
        blades: [],
        searchConfig: '',
        searchConfigLoading: false,
        processSearchCriteria: '',
        selectedKeys: '',
        newPermission: false,
        orderBy: '',
        id: null,
      };
    },

    hideForms(state) {
      return {
        ...state,
        id: null,
        searchConfig: '',
        processSearchCriteria: '',
        selectedKeys: '',
        processId: null,
      };
    },
  },
};

export default Model;
